import type { BillingOrdwayChargeModel, BillingOrdwayPlanModel } from '@customerio/hydra-ui-api';
import { BillingSystem } from '../types';
import type { BillingPlan } from '../types';
import { OrdwayBillingPeriod } from '../ordway/plans';
import { ProductName } from '../ordway/products';
import {
  findBaseOrdwayItem,
  findOrdwayItemByProductName,
  OrdwayChargeType,
  ordwayHighlights,
  ordwayIntervalDisplayName,
  parseOrdwayFeatureGroups,
} from '../ordway/utils';
import { PlanType } from '../plans';

/** Typed view of the loose `custom_fields` bag on an Ordway plan. */
interface OrdwayPlanCustomFields {
  cancellable?: boolean;
  plan_type?: PlanType;
  feature_groups?: string;
  is_free?: boolean;
}

/** Typed view of the loose `custom_fields` bag on an Ordway charge. */
interface OrdwayChargeCustomFields {
  plan_type?: PlanType;
}

/**
 * An Ordway plan transformed into the shared {@link BillingPlan} shape, with the raw
 * wire fields preserved side-by-side (mirroring the Ember model instance).
 */
export type OrdwayPlanBillingPlan = BillingOrdwayPlanModel &
  BillingPlan & { billingSystem: typeof BillingSystem.Ordway };

function planCustomFields(plan: BillingOrdwayPlanModel): OrdwayPlanCustomFields {
  return (plan.custom_fields ?? {}) as OrdwayPlanCustomFields;
}

function chargeCustomFields(charge: BillingOrdwayChargeModel): OrdwayChargeCustomFields {
  return (charge.custom_fields ?? {}) as OrdwayChargeCustomFields;
}

function planType(charges: BillingOrdwayChargeModel[]): PlanType {
  const charge = charges.find((candidate) => chargeCustomFields(candidate).plan_type != null);
  return chargeCustomFields(charge ?? ({} as BillingOrdwayChargeModel)).plan_type ?? PlanType.Essentials;
}

function basePrice(charges: BillingOrdwayChargeModel[]): number {
  const recurringCharges = charges.filter((charge) => charge.type !== OrdwayChargeType.Usage);

  return recurringCharges.reduce((accumulator, charge) => {
    const cents = Number.parseFloat(charge.list_price);
    return cents * 100 + accumulator;
  }, 0);
}

function includedUnits(charge: BillingOrdwayChargeModel | undefined): number {
  return Number.parseInt(charge?.included_units ?? '0', 10);
}

/** Transform an Ordway plan wire model into the shared {@link BillingPlan} shape. */
// fallow-ignore-next-line complexity — ported from Ember billing, complexity is inherent to the domain mapping
export function ordwayPlanToBillingPlan(plan: BillingOrdwayPlanModel): OrdwayPlanBillingPlan {
  const charges = plan.charges ?? [];
  const base = findBaseOrdwayItem(charges);

  const customersCharge = findOrdwayItemByProductName(charges, ProductName.JourneysCustomersOverages);
  const messagesCharge = findOrdwayItemByProductName(charges, ProductName.JourneysMessageOverages);
  const objectsCharge = findOrdwayItemByProductName(charges, ProductName.JourneysObjectsOverages);
  const profilesCharge = findOrdwayItemByProductName(charges, ProductName.JourneysProfilesOverages);
  const mauCharge = findOrdwayItemByProductName(charges, ProductName.MonthlyActiveUsers);

  const type = planType(charges);
  const groups = parseOrdwayFeatureGroups(planCustomFields(plan).feature_groups);

  const includedProfiles = includedUnits(profilesCharge);
  const includedMessages = includedUnits(messagesCharge);
  const includedMau = includedUnits(mauCharge);
  const mauOverageRate = mauCharge?.list_price ?? '';
  const price = basePrice(charges);

  return {
    ...plan,
    billingSystem: BillingSystem.Ordway,
    basePrice: price,
    intervalDisplayName: ordwayIntervalDisplayName(base?.billing_period),
    includedCustomers: includedUnits(customersCharge),
    includedMessages,
    includedObjects: includedUnits(objectsCharge),
    includedProfiles,
    includedMau,
    customerOverageRate: customersCharge?.list_price ?? '',
    messageOverageRate: messagesCharge?.list_price ?? '',
    objectOverageRate: objectsCharge?.list_price ?? '',
    profileOverageRate: profilesCharge?.list_price ?? '',
    mauOverageRate,
    canCancel: planCustomFields(plan).cancellable ?? false,
    planType: type,
    featureGroups: groups,
    name: plan.name,
    description: plan.description,
    isPaidAnnually: base?.billing_period === OrdwayBillingPeriod.Annually,
    annualPrice: price,
    highlights: ordwayHighlights(type, groups, includedProfiles, includedMessages),
    isTrial: planCustomFields(plan).is_free ?? false,
    isMauPricing: includedMau !== 0 && mauOverageRate !== '',
  };
}
